import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { loadIeltsSessions } from '../data/storage';

const WA_NUMBER = '201008182425';

const ALL_GROUP_SIZES = [
  { id:'private', label:'Private (1-on-1)' },
  { id:'2',       label:'2 Students' },
  { id:'3-4',     label:'3–4 Students' },
  { id:'4-8',     label:'4–8 Students' },
  { id:'8-10',    label:'8–10 Students' },
];
const IELTS_GROUP_SIZES = [
  { id:'private', label:'Private (1-on-1)' },
  { id:'group',   label:'Group Class' },
];

// Hardcoded learn items per CEFR level — used as fallback when DB has no learn list
const LEARN_BY_CEFR = {
  A1: ['Alphabet, numbers, and essential vocabulary','Simple greetings and self-introductions','Basic sentence structure (am / is / are)','Days, months, colours, family, and jobs','Reading simple signs and short messages'],
  A2: ['Past simple, present perfect, and future tenses','Shopping, travel, and everyday conversations','Writing short emails and messages','Describing experiences and making plans','Expanding vocabulary to 1,500+ words'],
  B1: ['Conditionals, passive voice, and reported speech','Formal vs. informal writing styles','Discussion skills and expressing opinions','Reading newspaper articles and short stories','Vocabulary for work, society, and current events'],
  B2: ['Complex grammar: inversion, cleft sentences, mixed conditionals','Academic reading and note-taking','Essay writing with cohesion and coherence','Idiomatic expressions and collocations','Listening to lectures and podcasts critically'],
  C1: ['Nuanced vocabulary and register variation','Advanced academic writing and argumentation','Stylistic analysis of authentic texts','Confident public speaking and debate','Preparation for Cambridge CAE / C1 Advanced'],
  IELTS: ['Writing Task 1 & Task 2 with AI paragraph feedback','Academic Reading strategies for speed and accuracy','Listening: form completion, multiple choice, matching','Speaking: all three parts with structured model answers','Full mock tests under timed exam conditions'],
};

const SIZES_BY_CEFR = { IELTS: IELTS_GROUP_SIZES };

const DAY_ORDER = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

function formatDays(days) {
  if (!days || days.length === 0) return '';
  const sorted = [...days].sort((a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b));
  const indices = sorted.map(d => DAY_ORDER.indexOf(d));
  const isConsec = indices.every((v, i) => i === 0 || v === indices[i-1] + 1);
  if (isConsec && sorted.length >= 3) return `${sorted[0]}–${sorted[sorted.length-1]}`;
  return sorted.join(', ');
}
function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${h===0?12:h>12?h-12:h}:${m.toString().padStart(2,'0')} ${h>=12?'PM':'AM'}`;
}
function formatTimezone(tz) {
  if (!tz) return '';
  const m = tz.match(/GMT[+-]\d+/);
  return m ? m[0] : tz;
}

function WAIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
}

function IELTSOutline() {
  const [open, setOpen] = useState(false);
  const GOLD = '#b8860b';
  const skills = [
    { icon:'🎧', name:'Listening', duration:'1 Week', items:['Smart strategies','Time management','Periodic exams','Daily assignments / instant feedback'] },
    { icon:'📖', name:'Reading',   duration:'1 Week', items:['Secrets of every question type','Handling challenges','Realistic exams','Daily assignments / instant feedback'] },
    { icon:'✍️', name:'Writing',   duration:'1–2 Weeks', items:['Writing reports/essays','Developing vocabulary & grammar','Accurate feedback','Writing and feedback during the lecture','Daily assignments / instant feedback'] },
    { icon:'🗣️', name:'Speaking',  duration:'1 Week', items:['Achieve Band 7+','Practical practice','Zoom mock exams','Daily assignments / instant feedback'] },
  ];
  return (
    <div style={{ marginBottom:14 }}>
      <button onClick={()=>setOpen(o=>!o)}
        style={{ background:'rgba(184,134,11,0.15)', border:'1px solid rgba(184,134,11,0.4)', borderRadius:8, padding:'9px 14px', color:'#f0d060', fontSize:12.5, fontWeight:600, cursor:'pointer', width:'100%', textAlign:'left', display:'flex', justifyContent:'space-between', alignItems:'center', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
        <span>📋 View Course Outline</span>
        <span style={{ fontSize:16 }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{ marginTop:10, background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.15)', borderRadius:10, padding:'16px' }}>
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:16, fontWeight:700, color:'#ffffff', marginBottom:4 }}>
            Intensive IELTS Course — Your Path to Band 7+ in 4–5 Weeks
          </div>
          <div style={{ fontSize:11.5, color:'rgba(255,255,255,0.5)', marginBottom:14, lineHeight:1.5 }}>
            Standard course: 6 weeks · Customized programs (Academic/General) adaptable to your level
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            {skills.map(sk => (
              <div key={sk.name} style={{ background:'rgba(184,134,11,0.1)', border:'1px solid rgba(184,134,11,0.25)', borderRadius:8, padding:'12px' }}>
                <div style={{ display:'flex', alignItems:'center', gap:7, marginBottom:6 }}>
                  <span style={{ fontSize:18 }}>{sk.icon}</span>
                  <div>
                    <div style={{ fontWeight:700, color:'#f0d060', fontSize:13 }}>{sk.name}</div>
                    <div style={{ fontSize:10, color:'rgba(240,208,96,0.6)', fontFamily:"'JetBrains Mono',monospace" }}>{sk.duration}</div>
                  </div>
                </div>
                <ul style={{ listStyle:'none', margin:0, padding:0 }}>
                  {sk.items.map((item,i) => (
                    <li key={i} style={{ display:'flex', alignItems:'flex-start', marginBottom:4 }}>
                      <span style={{ color:GOLD, marginRight:6, marginTop:3, fontSize:8, flexShrink:0 }}>◆</span>
                      <span style={{ fontSize:11.5, color:'rgba(255,255,255,0.75)', lineHeight:1.5 }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function CoursesPage({ onBack, onPlacementTest }) {
  const [courses,       setCourses]       = useState([]);
  const [tiers,         setTiers]         = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [selectedSize,  setSelectedSize]  = useState({});

  const activeSessions = loadIeltsSessions().filter(s => s.status === 'active');

  useEffect(() => {
    async function load() {
      const [c, t] = await Promise.all([
        supabase.from('courses').select('*').eq('active', true).order('sort_order'),
        supabase.from('subscription_tiers').select('*').eq('is_active', true).order('sort_order'),
      ]);
      const dbCourses = c.data || [];
      setCourses(dbCourses);
      setTiers(t.data || []);
      const sizes = {};
      dbCourses.forEach(course => {
        sizes[course.cefr_level || course.id] = 'private';
      });
      setSelectedSize(sizes);
      setLoading(false);
    }
    load();
  }, []);

  function getLearnItems(course) {
    if (course.learn_items && Array.isArray(course.learn_items)) return course.learn_items;
    return LEARN_BY_CEFR[course.cefr_level] || [course.description].filter(Boolean);
  }

  function getSizes(course) {
    return SIZES_BY_CEFR[course.cefr_level] || ALL_GROUP_SIZES;
  }

  function buildWAMessage(course, sizeId) {
    const sizes = getSizes(course);
    const groupLabel = sizes.find(s => s.id === sizeId)?.label || sizeId;
    let pref = '';
    if (sizeId === 'private') pref = 'I would like a private (1-on-1) lesson.';
    else if (sizeId === 'group') {
      const list = activeSessions.map(s => `${formatDays(s.days)}, ${formatTime(s.startTime)}–${formatTime(s.endTime)} (${formatTimezone(s.timezone)})`).join('; ');
      pref = list ? `I would like to join a group class. Available sessions: ${list}.` : 'I would like to join a group class.';
    } else {
      pref = `I would like a group class for ${groupLabel}.`;
    }
    return `Hello Mr. Ahmed Salem, I am interested in the ${course.cefr_level ? course.cefr_level + ' ' : ''}${course.name} course. ${pref} Please contact me to discuss details.`;
  }

  function openWA(course) {
    const key = course.cefr_level || course.id;
    const size = selectedSize[key] || 'private';
    const msg = buildWAMessage(course, size);
    window.open(`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(msg)}`, '_blank');
  }

  function setSize(course, sizeId) {
    const key = course.cefr_level || course.id;
    setSelectedSize(prev => ({ ...prev, [key]: sizeId }));
  }

  if (loading) return (
    <div style={{ minHeight:'100vh', background:'linear-gradient(135deg,#f0f4f8,#e4ecf7)', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ color:'#9aa3b8', fontFamily:"'JetBrains Mono',monospace", fontSize:13 }}>Loading courses…</div>
    </div>
  );

  return (
    <div style={{ minHeight:'100vh', background:'linear-gradient(135deg,#f0f4f8 0%,#e4ecf7 100%)', padding:'28px 16px' }}>
      <div style={{ maxWidth:980, margin:'0 auto' }}>
        <button onClick={onBack} style={{ background:'none', border:'none', color:'#9aa3b8', fontSize:11, cursor:'pointer', fontFamily:"'JetBrains Mono',monospace", letterSpacing:'.08em', marginBottom:20 }}>← Back</button>

        <div className="fade-up" style={{ marginBottom:28 }}>
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:28, fontWeight:700, color:'#1a2a4a', marginBottom:6 }}>Courses with Mr. Ahmed Salem</div>
          <div style={{ fontSize:13.5, color:'#5a6a88', lineHeight:1.65, maxWidth:560 }}>
            From complete beginner to IELTS Band 8 — choose your level and start learning. Select your preferred group size below each course, then contact Mr. Ahmed on WhatsApp.
          </div>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(290px,1fr))', gap:18 }}>
          {courses.map(course => {
            const isIELTS = course.cefr_level === 'IELTS';
            const hl = isIELTS;
            const cardBg     = hl ? 'linear-gradient(135deg,#1a2a4a,#2a3a6a)' : (course.bg_color || '#f8fafc');
            const cardBorder = hl ? '#4a6a9a' : (course.border_color || '#e8edf5');
            const txtColor   = hl ? '#ffffff' : '#1a2a4a';
            const subColor   = hl ? '#b0c8e8' : '#5a6a88';
            const learnColor = hl ? '#d0e4f8' : '#2a3a5a';
            const sectionColor = hl ? '#8ab0d8' : '#9aa3b8';
            const badgeBg    = hl ? 'rgba(255,255,255,0.15)' : `${course.color||'#1a2a4a'}22`;
            const badgeBorder= hl ? 'rgba(255,255,255,0.3)' : `${course.color||'#1a2a4a'}44`;
            const badgeColor = hl ? '#ffffff' : (course.color || '#1a2a4a');
            const pillBg     = hl ? 'rgba(255,255,255,0.08)' : '#f7f9fc';
            const pillBorder = hl ? 'rgba(255,255,255,0.2)' : '#dde3ed';
            const pillColor  = hl ? '#c8ddf8' : '#5a6a88';
            const pillActiveBg     = hl ? 'rgba(184,134,11,0.35)' : `${course.color||'#1a2a4a'}22`;
            const pillActiveBorder = hl ? '#d4a017' : (course.color || '#1a2a4a');
            const pillActiveColor  = hl ? '#ffd060' : (course.color || '#1a2a4a');
            const sizeKey = course.cefr_level || course.id;
            const cur = selectedSize[sizeKey] || 'private';
            const sizes = getSizes(course);
            const learnItems = getLearnItems(course);

            // IELTS tiers from DB (linked by cefr_level or course name match)
            const ieltsTiers = isIELTS ? tiers.filter(t =>
              t.course_id ? courses.find(c=>c.id===t.course_id && c.cefr_level==='IELTS') : (t.name.toLowerCase().includes('ielts'))
            ) : [];

            return (
              <div key={course.id} className="fade-up" style={{ background:cardBg, border:`1.5px solid ${cardBorder}`, borderRadius:14, padding:22, boxShadow:hl?'0 6px 28px rgba(26,42,74,0.22)':'0 2px 12px rgba(26,42,74,0.08)', display:'flex', flexDirection:'column' }}>

                {/* Badge + emoji */}
                <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
                  <span style={{ fontSize:26 }}>{course.icon || '📚'}</span>
                  {course.cefr_level && (
                    <div style={{ display:'inline-block', background:badgeBg, color:badgeColor, border:`1px solid ${badgeBorder}`, borderRadius:6, padding:'2px 9px', fontSize:11, fontWeight:700, fontFamily:"'JetBrains Mono',monospace", letterSpacing:'.1em' }}>
                      {course.cefr_level}
                    </div>
                  )}
                </div>

                {/* Title / description */}
                <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:21, fontWeight:700, color:txtColor, marginBottom:4 }}>{course.name}</div>
                <div style={{ fontSize:12.5, color:subColor, marginBottom:14, lineHeight:1.55 }}>{course.description || ''}</div>

                {/* What you'll learn */}
                {learnItems.length > 0 && (
                  <>
                    <div style={{ fontSize:11, fontFamily:"'JetBrains Mono',monospace", letterSpacing:'.13em', textTransform:'uppercase', color:sectionColor, marginBottom:6 }}>What you'll learn</div>
                    <ul style={{ listStyle:'none', margin:'0 0 16px', padding:0 }}>
                      {learnItems.map((l, i) => (
                        <li key={i} style={{ display:'flex', alignItems:'flex-start', marginBottom:5 }}>
                          <span style={{ display:'inline-block', width:5, height:5, borderRadius:'50%', background:'#b8860b', marginRight:8, marginTop:6, flexShrink:0 }}/>
                          <span style={{ fontSize:12.5, color:learnColor, lineHeight:1.65 }}>{l}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                {/* IELTS: active sessions */}
                {isIELTS && (
                  <div style={{ marginBottom:14 }}>
                    <div style={{ fontSize:11, fontFamily:"'JetBrains Mono',monospace", letterSpacing:'.13em', textTransform:'uppercase', color:sectionColor, marginBottom:8 }}>Group Class Schedule</div>
                    {activeSessions.length > 0 ? activeSessions.map(sess => (
                      <div key={sess.id} style={{ background:'rgba(184,134,11,0.15)', border:'1px solid rgba(184,134,11,0.3)', borderRadius:7, padding:'8px 12px', marginBottom:7, fontSize:12.5, color:'#f0d060', fontWeight:500 }}>
                        📅 {formatDays(sess.days)} · {formatTime(sess.startTime)}–{formatTime(sess.endTime)} ({formatTimezone(sess.timezone)})
                        {sess.label && <span style={{ color:'rgba(240,208,96,0.75)', marginLeft:6 }}>— {sess.label}</span>}
                      </div>
                    )) : (
                      <div style={{ fontSize:12.5, color:'rgba(255,255,255,0.45)', fontStyle:'italic' }}>Contact Mr. Ahmed for available times.</div>
                    )}
                  </div>
                )}

                {/* IELTS: course outline */}
                {isIELTS && <IELTSOutline/>}

                {/* IELTS: package pricing from DB */}
                {isIELTS && ieltsTiers.length > 0 && (
                  <div style={{ marginBottom:16 }}>
                    <div style={{ fontSize:11, fontFamily:"'JetBrains Mono',monospace", letterSpacing:'.13em', textTransform:'uppercase', color:sectionColor, marginBottom:8 }}>Packages</div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:10 }}>
                      {ieltsTiers.slice(0,4).map((pkg, idx) => (
                        <div key={pkg.id} style={{ background:idx===1?'rgba(184,134,11,0.18)':'rgba(255,255,255,0.08)', border:`1px solid ${idx===1?'rgba(184,134,11,0.5)':'rgba(255,255,255,0.15)'}`, borderRadius:8, padding:'10px 12px' }}>
                          <div style={{ fontSize:11, color:idx===1?'#d4a017':'rgba(255,255,255,0.6)', fontWeight:600, marginBottom:3 }}>{pkg.name}</div>
                          {pkg.show_price === true && pkg.price ? (
                            <div style={{ fontSize:19, fontWeight:700, color:idx===1?'#f0d060':'#ffffff', fontFamily:"'Cormorant Garamond',serif", lineHeight:1.1 }}>{pkg.price} {pkg.currency}</div>
                          ) : (
                            <div style={{ fontSize:12, color:'rgba(255,255,255,0.5)', fontStyle:'italic' }}>Contact for pricing</div>
                          )}
                          <div style={{ fontSize:10.5, color:idx===1?'rgba(240,208,96,0.7)':'rgba(255,255,255,0.4)', marginTop:3 }}>
                            {pkg.duration_unit === 'unlimited' ? 'Unlimited' : `${pkg.duration_value} ${pkg.duration_unit}`}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div style={{ fontSize:11.5, color:'rgba(255,255,255,0.45)', fontStyle:'italic', textAlign:'center' }}>
                      Contact Mr. Ahmed for current pricing and availability.
                    </div>
                  </div>
                )}

                {/* Group size selector */}
                <div style={{ fontSize:11, fontFamily:"'JetBrains Mono',monospace", letterSpacing:'.13em', textTransform:'uppercase', color:sectionColor, marginBottom:8 }}>Select group size</div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:16 }}>
                  {sizes.map(sz => {
                    const active = cur === sz.id;
                    return (
                      <button key={sz.id} onClick={() => setSize(course, sz.id)}
                        style={{ fontSize:11.5, padding:'5px 10px', borderRadius:20, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", transition:'all .15s',
                          background: active ? pillActiveBg : pillBg,
                          border:     `1.5px solid ${active ? pillActiveBorder : pillBorder}`,
                          color:      active ? pillActiveColor : pillColor,
                          fontWeight: active ? 700 : 400,
                        }}>
                        {sz.label}
                      </button>
                    );
                  })}
                </div>

                {/* WhatsApp CTA */}
                <button onClick={() => openWA(course)} style={{ background:'#25d366', color:'#fff', border:'none', padding:'12px 0', borderRadius:8, fontSize:13.5, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", display:'flex', alignItems:'center', justifyContent:'center', gap:9, width:'100%', marginTop:'auto', boxShadow:'0 2px 8px rgba(37,211,102,0.3)' }}>
                  <WAIcon/>
                  Enquire on WhatsApp
                </button>
              </div>
            );
          })}
        </div>

        {onPlacementTest && (
          <div style={{ textAlign:'center', marginTop:32, color:'#9aa3b8', fontSize:12, fontFamily:"'JetBrains Mono',monospace" }}>
            Not sure which level?{' '}
            <button onClick={onPlacementTest} style={{ background:'none', border:'none', color:'#b8860b', fontSize:12, cursor:'pointer', fontFamily:"'JetBrains Mono',monospace", textDecoration:'underline', padding:0 }}>
              Take the free Placement Test
            </button>{' '}
            first.
          </div>
        )}
      </div>
    </div>
  );
}
